import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { TabButtons } from 'legacy/lut/library/tabButtons'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { NextButton } from 'partner360/components/QBR/components/qbrDocumentDetails/components/nextButton/NextButton'
import { FinancialDataVisualizer } from 'partner360/components/QBR/shared/financialDataVisualizer/FinancialDataVisualizer'
import {
  collapsePlanAchievementTableRow,
  expandPlanAchievementTableRow,
  selectPlanAchievementDependOnSettings,
  selectQbrCurrency,
  selectQBRDocumentFormData,
  selectQbrNetNdp,
  selectRounding,
} from 'partner360/features/QBR'
import { DEFAULT_ROUNDING } from 'partner360/features/QBR/configs'
import { selectQbrSelectedPeriod } from 'partner360/features/QBR/selectors/selectQbrSelectedPeriod'
import { IQBRPlanAchievementDependOnSettings } from 'partner360/models/qbr/IQBRPlanAchievementDependOnSettings'

import { useQbrSave } from '../../../hooks/useQbrSave'
import { QBRErrorModal } from '../settingsStep/components/qbrErrorModal/QBRErrorModal'

import { FinanceType } from './configs'
import { getColumnsMoneyShortedView } from './helpers/getColumnsMoneyShortedView'
import { getColumnsMoneyView } from './helpers/getColumnsMoneyView'
import { getColumnsUnitsShortedView } from './helpers/getColumnsUnitsShortedView'
import { getColumnsUnitsView } from './helpers/getColumnsUnitsView'

import styles from './PlanAchievement.module.scss'

export const PlanAchievement = () => {
  const data = useSelector(selectPlanAchievementDependOnSettings)
  const document = useSelector(selectQBRDocumentFormData)
  const rounding = useSelector(selectRounding)
  const netNdp = useSelector(selectQbrNetNdp)
  const currency = useSelector(selectQbrCurrency)
  const selectedPeriod = useSelector(selectQbrSelectedPeriod)
  const [financialType, setFinancialType] = useState(FinanceType.Money)

  const dispatch = useAppDispatch()

  const { isSavingProgress, onNextButtonClick, showErrorModal, onCloseErrorModal } = useQbrSave()

  const showUpToDateColumn = useMemo(() => {
    if (!document) {
      return false
    }

    return document.DocumentHideUtD === 0
  }, [document])

  const firstColumnHeader = useMemo(() => {
    const isUnits = financialType === FinanceType.Units

    if (rounding === DEFAULT_ROUNDING) {
      return isUnits ? 'Units' : `${netNdp} ${currency}`
    }

    return isUnits ? 'Units' : `${netNdp} ${currency} ${rounding}`
  }, [financialType, rounding, netNdp, currency])

  const expandedNodes = useMemo(() => {
    return data.filter((el) => el.IsExpanded)
  }, [data])

  const selectedPeriodFYear = useMemo(() => selectedPeriod.fYear, [selectedPeriod.fYear])
  const selectedPeriodFQuarters = useMemo(
    () => selectedPeriod.fQuarters.map(({ fQuarter }) => fQuarter),
    [selectedPeriod.fQuarters]
  )

  const columns = useMemo(() => {
    if (financialType === FinanceType.Money) {
      return getColumnsMoneyView({
        rounding,
        selectedPeriodFQuarters,
        showUpToDateColumn,
      })
    }

    return getColumnsUnitsView({
      selectedPeriodFQuarters,
      showUpToDateColumn,
    })
  }, [financialType, rounding, selectedPeriodFQuarters, showUpToDateColumn])

  const columnsForCharts = useMemo(() => {
    if (financialType === FinanceType.Money) {
      return getColumnsMoneyShortedView({
        rounding,
        currentFYear: selectedPeriodFYear,
        selectedPeriodFQuarters,
      })
    }

    return getColumnsUnitsShortedView({
      currentFYear: selectedPeriodFYear,
      selectedPeriodFQuarters,
    })
  }, [financialType, rounding, selectedPeriodFYear, selectedPeriodFQuarters])

  const getBarsSettings = useCallback(
    (item: IQBRPlanAchievementDependOnSettings) => {
      if (financialType === FinanceType.Money) {
        return [
          {
            name: 'Plans',
            color: '#4858F5',
            data: [
              item.PrevYearQ1Plans,
              item.PrevYearQ2Plans,
              item.PrevYearQ3Plans,
              item.PrevYearQ4Plans,
              item.CurrentYearQ1Plans,
              item.CurrentYearQ2Plans,
              item.CurrentYearQ3Plans,
              item.CurrentYearQ4Plans,
            ],
          },
          {
            name: 'Actuals',
            color: '#00D072',
            data: [
              item.PrevYearQ1Actuals,
              item.PrevYearQ2Actuals,
              item.PrevYearQ3Actuals,
              item.PrevYearQ4Actuals,
              item.CurrentYearQ1Actuals,
              item.CurrentYearQ2Actuals,
              item.CurrentYearQ3Actuals,
              item.CurrentYearQ4Actuals,
            ],
          },
        ]
      }

      return [
        {
          name: 'Plans',
          color: '#4858F5',
          data: [
            item.PrevYearQ1PlansUnits,
            item.PrevYearQ2PlansUnits,
            item.PrevYearQ3PlansUnits,
            item.PrevYearQ4PlansUnits,
            item.CurrentYearQ1PlansUnits,
            item.CurrentYearQ2PlansUnits,
            item.CurrentYearQ3PlansUnits,
            item.CurrentYearQ4PlansUnits,
          ],
        },
        {
          name: 'Actuals',
          color: '#00D072',
          data: [
            item.PrevYearQ1ActualsUnits,
            item.PrevYearQ2ActualsUnits,
            item.PrevYearQ3ActualsUnits,
            item.PrevYearQ4ActualsUnits,
            item.CurrentYearQ1ActualsUnits,
            item.CurrentYearQ2ActualsUnits,
            item.CurrentYearQ3ActualsUnits,
            item.CurrentYearQ4ActualsUnits,
          ],
        },
      ]
    },
    [financialType]
  )

  const getLinesSettings = useCallback(
    (item: IQBRPlanAchievementDependOnSettings) => {
      if (financialType === FinanceType.Money) {
        return [
          {
            name: 'Plan%',
            color: '#FFBD3A',
            data: [
              item.PrevYearQ1ActualsToPlans,
              item.PrevYearQ2ActualsToPlans,
              item.PrevYearQ3ActualsToPlans,
              item.PrevYearQ4ActualsToPlans,
              item.CurrentYearQ1ActualsToPlans,
              item.CurrentYearQ2ActualsToPlans,
              item.CurrentYearQ3ActualsToPlans,
              item.CurrentYearQ4ActualsToPlans,
            ],
          },
          {
            name: 'YoY%',
            color: '#FF63BB',
            data: [
              item.PrevYearQ1YoYActuals,
              item.PrevYearQ2YoYActuals,
              item.PrevYearQ3YoYActuals,
              item.PrevYearQ4YoYActuals,
              item.CurrentYearQ1YoYActuals,
              item.CurrentYearQ2YoYActuals,
              item.CurrentYearQ3YoYActuals,
              item.CurrentYearQ4YoYActuals,
            ],
          },
        ]
      }
      return [
        {
          name: 'Plan%',
          color: '#FFBD3A',
          data: [
            item.PrevYearQ1ActualsToPlansUnits,
            item.PrevYearQ2ActualsToPlansUnits,
            item.PrevYearQ3ActualsToPlansUnits,
            item.PrevYearQ4ActualsToPlansUnits,
            item.CurrentYearQ1ActualsToPlansUnits,
            item.CurrentYearQ2ActualsToPlansUnits,
            item.CurrentYearQ3ActualsToPlansUnits,
            item.CurrentYearQ4ActualsToPlansUnits,
          ],
        },
        {
          name: 'YoY%',
          color: '#FF63BB',
          data: [
            item.PrevYearQ1YoYActualsUnits,
            item.PrevYearQ2YoYActualsUnits,
            item.PrevYearQ3YoYActualsUnits,
            item.PrevYearQ4YoYActualsUnits,
            item.CurrentYearQ1YoYActualsUnits,
            item.CurrentYearQ2YoYActualsUnits,
            item.CurrentYearQ3YoYActualsUnits,
            item.CurrentYearQ4YoYActualsUnits,
          ],
        },
      ]
    },
    [financialType]
  )

  const onExpand = useCallback(
    (keyField: string, keyFieldValue: string | number, hierarchyField: string) => {
      dispatch(expandPlanAchievementTableRow({ keyField, keyFieldValue, hierarchyField }))
    },
    [dispatch]
  )

  const onCollapse = useCallback(
    (keyField: string, keyFieldValue: string | number, hierarchyField: string) => {
      dispatch(collapsePlanAchievementTableRow({ keyField, keyFieldValue, hierarchyField }))
    },
    [dispatch]
  )

  const tabs = useMemo(() => {
    return [
      {
        id: FinanceType.Money,
        title: () => <div>Financials</div>,
        content: () => (
          <FinancialDataVisualizer
            className={styles.table}
            currencyLabel={firstColumnHeader}
            chartLabelNode={firstColumnHeader}
            data={data}
            columns={columns}
            chartNameField={'Name'}
            getBarsSettings={getBarsSettings}
            getLinesSettings={getLinesSettings}
            onExpand={onExpand}
            onCollapse={onCollapse}
            expandedNodes={expandedNodes}
            columnsForCharts={columnsForCharts}
            showUpToDateColumn={showUpToDateColumn}
            selectedPeriodFQuarters={selectedPeriodFQuarters}
            selectedPeriodFYear={selectedPeriodFYear}
          />
        ),
      },
      {
        id: FinanceType.Units,
        title: () => <div>Units</div>,
        content: () => (
          <FinancialDataVisualizer
            className={styles.table}
            currencyLabel={firstColumnHeader}
            chartLabelNode={firstColumnHeader}
            data={data}
            columns={columns}
            chartNameField={'Name'}
            getBarsSettings={getBarsSettings}
            getLinesSettings={getLinesSettings}
            onExpand={onExpand}
            onCollapse={onCollapse}
            expandedNodes={expandedNodes}
            columnsForCharts={columnsForCharts}
            showUpToDateColumn={showUpToDateColumn}
            selectedPeriodFQuarters={selectedPeriodFQuarters}
            selectedPeriodFYear={selectedPeriodFYear}
          />
        ),
      },
    ]
  }, [
    firstColumnHeader,
    getBarsSettings,
    columns,
    data,
    expandedNodes,
    onCollapse,
    onExpand,
    columnsForCharts,
    getLinesSettings,
    selectedPeriodFQuarters,
    selectedPeriodFYear,
    showUpToDateColumn,
  ])

  const onChangeFinancialType = useCallback((value: string) => {
    setFinancialType(value as FinanceType)
  }, [])

  return (
    <div>
      <TabButtons onChange={onChangeFinancialType} value={financialType} items={tabs} />
      <NextButton onClick={onNextButtonClick} />
      <QBRErrorModal
        show={showErrorModal}
        onCloseButtonClick={onCloseErrorModal}
        isButtonDisabled={isSavingProgress}
      />
    </div>
  )
}
